import { inject, Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { ModalController } from '@ionic/angular/standalone';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ForceUpdateSheetComponent } from 'src/shared/force-update-sheet/force-update-sheet.component';
import { FirestoreRouterService } from './firestore-router.service';

interface ForceUpdateConfig {
  appVersion: string;
  url: string;
}

@Injectable({ providedIn: 'root' })
export class ForceUpdateService {
  private db        = inject(FirestoreRouterService);
  private modalCtrl = inject(ModalController);

  async check(): Promise<void> {
    const platform = Capacitor.getPlatform();
    if (platform !== 'android' && platform !== 'ios') return;

    const docId = platform === 'ios' ? 'force_update_ios' : 'force_update_android';

    let config: ForceUpdateConfig | undefined;
    try {
      config = await firstValueFrom(this.db.watchDocument<ForceUpdateConfig>('config', docId));
    } catch {
      return;
    }

    if (!config?.appVersion) return;

    const currentVersion = environment.appVersion;
    if (this.isOutdated(currentVersion, config.appVersion)) {
      await this.showUpdateSheet(config.url);
    }
  }

  private isOutdated(current: string, required: string): boolean {
    const toparts = (v: string) => v.split('.').map(n => parseInt(n, 10) || 0);
    const [ca, cb, cc] = toparts(current);
    const [ra, rb, rc] = toparts(required);
    if (ca !== ra) return ca < ra;
    if (cb !== rb) return cb < rb;
    return cc < rc;
  }

  private async showUpdateSheet(url: string): Promise<void> {
    const existing = await this.modalCtrl.getTop();
    if (existing) return;

    const modal = await this.modalCtrl.create({
      component: ForceUpdateSheetComponent,
      componentProps: { url },
      backdropDismiss: false,
      canDismiss: false,
      cssClass: 'force-update-sheet',
    });
    await modal.present();
  }
}
